/* eslint-disable no-unused-vars */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../Sidebar';
import ComboBusca from '../../ComboBusca';
import { ArrowLeft, Info } from 'lucide-react';


// =====================================================================
// Medicina do Trabalho — Lançamento de Atestados.
//
// Onde o médico do trabalho registra o atestado apresentado e os dias que
// efetivamente concedeu. Renomeada em 2026-07-28 (era
// `MedicinaFormsAtestadoMedico_Questionario`): o antigo sub-hub "Formulários"
// morreu e virou o sub-módulo **Revisão de Atestados**, que tem duas telas —
// esta (Lançamento) e a Consulta. Registrar a revisão de um atestado não é
// preencher formulário.
//
// Hoje só é usada EMBUTIDA (`MedicinaRevisaoAtestados.js`); os trechos sob
// `!embedded` são o modo avulso legado, mantidos por não custarem nada.
// =====================================================================
function MedicinaAtestadosLancamento({ embedded = false }) {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth <= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [showInfo, setShowInfo] = useState(false);

  // Lógica de Margem Dinâmica para Celular
  const getMarginLeft = () => {
    if (window.innerWidth <= 480) return isCollapsed ? '70px' : '0px';
    return isCollapsed ? '100px' : '260px';
  };

  // --- ESTADO 1: Dados do Cabeçalho ---
  const [formData, setFormData] = useState({
    loja: '',
    data: '',
    nomeFuncionario: '',
    // Chave do funcionário (dtbConfere). É ELA que vai para o banco — o nome
    // continua não sendo gravado. A Consulta resolve o nome na leitura.
    idFuncionario: '',
    nomeCargo: '',
  });

  // --- ESTADO 2: Dados da API ---
  const [funcionariosPorLoja, setFuncionariosPorLoja] = useState({}); // Estrutura: { "Loja1": ["Func1"], "Loja2": ["Func2"] }
  const [funcionariosAtivos, setFuncionariosAtivos] = useState([]); // Lista de funcionários para o dropdown
  const [cargos, setCargos] = useState([]); // Estrutura: ["Cargo1", "Cargo2"]
  const [statusCarregamento, setStatusCarregamento] = useState('');

  // --- ESTADO 3: Respostas do Questionário ---
  // Após a minimização LGPD (2026-06-19): só as perguntas que ficam.
  // q1→P_1 (atendimento) · q2→P_2 (tipo) · q3→P_3 (dias original) ·
  // q4→P_4 (dias concedidos) · q8→P_8 (entrega). CID/Motivo/Medicação e as
  // datas abonadas foram removidos.
  const [questionario, setQuestionario] = useState({
    q1: '',
    q2: '',
    q3: '',
    q4: '',
    q8: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- ATUALIZAÇÃO DAS RESPOSTAS DO QUESTIONÁRIO ---
  //
  // Piso dos campos de dias (2026-07-28):
  //   q3 (dias no documento original) → mínimo 1. Atestado de ZERO dia não
  //      existe: se o documento não afasta ninguém, não é atestado. Havia 4
  //      registros assim no histórico, que ficavam fora dos dois cenários da
  //      Consulta (nem ">1 dia", nem "de 1 dia") e faziam os cards não somar.
  //   q4 (dias concedidos) → mínimo 0, de propósito. Zero aqui é a NEGATIVA
  //      integral do médico do trabalho, que é decisão legítima e frequente
  //      (231 casos no histórico). Travar em 1 apagaria justamente o ato que
  //      a Consulta existe para medir.
  const handleChange = (e) => {
    const { name, value } = e.target;
    const piso = name === 'q3' ? 1 : 0;
    if ((name === 'q3' || name === 'q4') && value !== '' && Number(value) < piso) {
      setQuestionario((prev) => ({ ...prev, [name]: piso }));
    } else {
      setQuestionario((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Troca do TIPO de atendimento (q2). Define quais campos aparecem abaixo e
  // LIMPA os condicionais — assim, ao mudar de "Atestado" para "Consulta", os
  // dias/atendimento/entrega do tipo anterior não vão parar no registro.
  const handleTipoChange = (e) => {
    const novoTipo = e.target.value;
    setQuestionario((prev) => ({ ...prev, q2: novoTipo, q1: '', q3: '', q4: '', q8: '' }));
  };

  // useEffect para responsividade (Já estava correto) 
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // --- useEffect CORRIGIDO para buscar dados da API ---
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    const fetchData = async (url) => {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`Erro ao carregar ${url}`);
      return response.json();
    };

    Promise.all([
      fetchData('/api/atestados/funcionarios'),
      fetchData('/api/atestados/funcionarios_cargos')
    ])
    .then(([funcionariosData, cargosData]) => {
      // Salva os dados brutos. O segundo useEffect processará a lista.
      setFuncionariosPorLoja(funcionariosData); 
      setCargos(cargosData); 
    })
    .catch(error => {
      console.error("Erro na API:", error);
      if (error.message.includes('Sessão inválida')) navigate('/login');
    });

    // ATENÇÃO: A dependência DEVE ser apenas [navigate]. 
    // Se colocar funcionariosPorLoja ou cargos aqui, causará o loop infinito!
  }, [navigate]);

  // --- 2. EFEITO DE FILTRO E ORDENAÇÃO (Roda quando a Loja ou os Dados mudarem) ---
  useEffect(() => {
    // Se ainda não houver dados, não faz nada
    if (!funcionariosPorLoja || Object.keys(funcionariosPorLoja).length === 0) return;

    let listaFiltrada = [];

    if (formData.loja && funcionariosPorLoja[formData.loja]) {
      // Pega apenas os funcionários da loja selecionada
      listaFiltrada = [...funcionariosPorLoja[formData.loja]];
    } else {
      // Caso não tenha loja, achata todas as listas de todas as lojas
      Object.values(funcionariosPorLoja).forEach(lista => {
        if (Array.isArray(lista)) {
          lista.forEach(obj => {
            if (obj && obj.nome) listaFiltrada.push(obj);
          });
        }
      });
    }

    // Ordenação Segura (proteção contra undefined)
    const listaOrdenada = listaFiltrada.sort((a, b) => 
      (a.nome || "").localeCompare(b.nome || "")
    );

    setFuncionariosAtivos(listaOrdenada);

  }, [formData.loja, funcionariosPorLoja]);

  // Função para atualizar o cabeçalho (Já estava correta)
  const handleFormChange = (e) => {
  const { name, value } = e.target;
  
  setFormData((prev) => {
    const newData = { ...prev, [name]: value };

    // FILTRO AUTOMÁTICO DE CARGO (e da chave do funcionário)
    if (name === 'nomeFuncionario') {
      const func = funcionariosAtivos.find(f => f.nome === value);
      if (func) {
        newData.nomeCargo = func.cargo;
        newData.idFuncionario = func.id;
      } else {
        newData.nomeCargo = '';
        newData.idFuncionario = '';
      }
    }

    if (name === 'loja') {
      newData.nomeFuncionario = '';
      newData.nomeCargo = '';
      newData.idFuncionario = '';
    }
    
    return newData;
  });
};

  // --- RENDERIZAÇÃO DAS PERGUNTAS (condicional ao tipo de atendimento) ---
  // Regras (2026-06-19):
  //   - Atestado            → Assistencial + Dias (original/concedido) + Entrega
  //   - Declaração de Horas → Assistencial + Entrega (sem dias)
  //   - Consulta            → nada além do tipo (vira só um registro)
  const renderPerguntas = () => {
    const tipo = questionario.q2;
    const temDocumento = tipo === 'Atestado' || tipo === 'Declaracao de Horas';
    const ehAtestado = tipo === 'Atestado';

    // O CSS .radio-group não tinha estilo (radios saíam colados): espaço aqui.
    const grupoRadios = { display: 'flex', flexWrap: 'wrap', gap: '10px 28px', alignItems: 'center', marginTop: '4px' };
    const labelRadio = { display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' };

    return (
      <>
        {/* Tipo de atendimento — define o que aparece abaixo */}
        <div className="question-group">
          <label className="question-label">Tipo de atendimento:</label>
          <div className="radio-group" style={grupoRadios}>
            <label style={labelRadio}>
              <input type="radio" name="q2" value="Atestado" onChange={handleTipoChange} checked={tipo === 'Atestado'} required />
              Atestado
            </label>
            <label style={labelRadio}>
              <input type="radio" name="q2" value="Declaracao de Horas" onChange={handleTipoChange} checked={tipo === 'Declaracao de Horas'} />
              Declaração de Horas
            </label>
            <label style={labelRadio}>
              <input type="radio" name="q2" value="Consulta" onChange={handleTipoChange} checked={tipo === 'Consulta'} />
              Consulta
            </label>
          </div>
        </div>

        {/* Assistencial — só quando há documento (atestado ou declaração) */}
        {temDocumento && (
          <div className="question-group">
            <label className="question-label">Assistencial:</label>
            <div className="radio-group" style={grupoRadios}>
              <label style={labelRadio}>
                <input type="radio" name="q1" value="SUS" onChange={handleChange} checked={questionario.q1 === 'SUS'} required />
                SUS
              </label>
              <label style={labelRadio}>
                <input type="radio" name="q1" value="Assistencial" onChange={handleChange} checked={questionario.q1 === 'Assistencial'} />
                Assistencial
              </label>
            </div>
          </div>
        )}

        {/* Dias — só para ATESTADO (a redução de dias só existe aqui) */}
        {ehAtestado && (
          <>
            <div className="question-group">
              <label htmlFor="q3" className="question-label">Quantidade de dias no documento original:</label>
              {/* min=1: o navegador barra o envio com 0 (ou vazio). */}
              <input id="q3" type="number" name="q3" min="1" step="1" value={questionario.q3} onChange={handleChange} placeholder="Mínimo de 1 dia" className="form-input" required />
            </div>
            <div className="question-group">
              <label htmlFor="q4" className="question-label">Quantidade de dias concedidos pelo médico do trabalho:</label>
              {/* min=0: zero é a negativa integral do médico, não erro. */}
              <input id="q4" type="number" name="q4" min="0" step="1" value={questionario.q4} onChange={handleChange} placeholder="Pode ser 0, se nenhum dia foi concedido" className="form-input" required />
            </div>
          </>
        )}

        {/* Entrega — só quando há documento */}
        {temDocumento && (
          <div className="question-group">
            <label className="question-label">Entrega do documento:</label>
            <div className="radio-group" style={grupoRadios}>
              <label style={labelRadio}>
                <input type="radio" name="q8" value="Dentro do prazo" onChange={handleChange} checked={questionario.q8 === 'Dentro do prazo'} required />
                Dentro do prazo
              </label>
              <label style={labelRadio}>
                <input type="radio" name="q8" value="Fora do prazo" onChange={handleChange} checked={questionario.q8 === 'Fora do prazo'} />
                Fora do prazo
              </label>
            </div>
          </div>
        )}

        {/* Consulta pura → só registro */}
        {tipo === 'Consulta' && (
          <p style={{ color: '#64748b', fontSize: '14px', padding: '8px 0' }}>
            Consulta sem atestado — apenas o registro será salvo (sem dias nem entrega).
          </p>
        )}
      </>
    );
  };
    
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;
    setIsSubmitting(true);

    // Prepara os dados para enviar ao back-end
    const now = new Date();
    // Formata a data para 'YYYY-MM-DD HH:mm:ss'
    const formattedTimestamp = now.getFullYear() + '-' +
                         String(now.getMonth() + 1).padStart(2, '0') + '-' +
                         String(now.getDate()).padStart(2, '0') + ' ' +
                         String(now.getHours()).padStart(2, '0') + ':' +
                         String(now.getMinutes()).padStart(2, '0') + ':' +
                         String(now.getSeconds()).padStart(2, '0');

    // Prepara os dados para enviar ao back-end
    const questionarioData = {
      LOJA: formData.loja || '',
      DATA: formData.data || '', // já em YYYY-MM-DD
      NOME_CARGO: formData.nomeCargo || '',
      ID_FUNCIONARIO: formData.idFuncionario || '',
      TIMESTAMP: formattedTimestamp,

      // Mapeia o estado 'questionario' (q1→P_1, q2→P_2, …). O NOME do
      // funcionário continua NÃO sendo enviado — vai só a CHAVE
      // (ID_FUNCIONARIO), e a Consulta resolve nome e cargo na leitura.
      ...Object.keys(questionario).reduce((acc, key) => {
        const p_key = `P_${key.replace('q', '')}`;
        acc[p_key] = questionario[key] || '';
        return acc;
      }, {}),
    };

    // Depuração: Log dos dados enviados
    console.log('Dados enviados:', JSON.stringify(questionarioData, null, 2));

    // Envia os dados para o back-end
    try {
      // 1. Pega o token salvo no localStorage
      const token = localStorage.getItem('authToken');

      // 2. Validação: Se não houver token, não prossiga.
      if (!token) {
        throw new Error('Você não está autenticado. Faça o login novamente.');
      }

      // 3. Faz a chamada para a API com a URL correta e o token
      const response = await fetch('/api/atestados/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                   'Authorization': `Bearer ${token}`
         },
        body: JSON.stringify(questionarioData),
      });

      if (response.ok) {
        console.log('Questionário enviado com sucesso:', await response.text());
        //alert('Questionário enviado com sucesso!');
        navigate('/agradecimento'); // Ou para a home, se preferir
        
      } else {
        alert('Erro ao enviar.');
        setIsSubmitting(false);
      }
    } catch (err) {
    alert('Erro de conexão.');
    setIsSubmitting(false); // DESTRAVA O BOTÃO
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={embedded ? { width: '100%' } : { display: 'flex', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {!embedded && <Sidebar isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />}
      
      <div style={{ flex: 1, marginLeft: embedded ? 0 : getMarginLeft(), transition: 'margin-left 0.3s ease' }}>
        
        {/* Navbar Simples Embutida — oculta quando embutido no módulo (o hub já tem cabeçalho) */}
        {!embedded && (
        <div style={{ height: '80px', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', padding: '0 40px', position: 'sticky', top: 0, zIndex: 10 }}>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#2a0f0f' }}>Preenchimento de Questionário</h2>
        </div>
        )}

        <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>
            
          {/* BOTÃO DE VOLTAR — oculto quando embutido (o sub-hub fornece "Voltar aos formulários") */}
          {!embedded && (
          <button onClick={() => navigate('/medicina')} style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#64748b', marginBottom: '20px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
              <ArrowLeft size={16} /> Voltar para Central de Formulários
          </button>
          )}

          <div style={{ backgroundColor: '#fff', padding: '40px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)' }}>
              
              <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                  {/* Se tiver os logotipos, pode manter as suas tags de imagem originais aqui */}
                  <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#24e11d', marginBottom: '10px' }}>Controle de Atestados Médicos</h1>
                  <p style={{ color: '#64748b' }}>Registro e controle de atestados médicos</p>
              </div>

              {/* Caixa Informativa */}
              <div style={{ backgroundColor: '#f5f3ff', borderLeft: '4px solid #24e11d', padding: '16px 20px', borderRadius: '8px', marginBottom: '30px' }}>
                  <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#12be29', fontSize: '15px', fontWeight: '700', margin: '0 0 10px 0' }}>
                      <Info size={18} /> Importante
                  </h4>
                  {/* Texto alinhado ao que o formulário REALMENTE coleta após a
                      minimização LGPD (2026-06-19): sem motivo, CID ou período. */}
                  <p style={{ color: '#49951d', fontSize: '14px', lineHeight: '1.6', margin: 0 }}>
                      Este formulário destina-se ao registro dos atestados apresentados pelos colaboradores
                      e à validação do médico do trabalho: a quantidade de dias do documento original,
                      a quantidade de dias efetivamente concedida e se a entrega foi feita dentro ou fora
                      do prazo. Também registra as declarações de horas e as consultas, quando solicitadas.
                  </p>
              </div>

              {/* SEU FORMULÁRIO ORIGINAL */}
              <form onSubmit={handleSubmit} className="form">
            <div className="form-group">
              <label htmlFor="loja" className="label">Loja</label>
              <select
                id="loja"
                name="loja"
                value={formData.loja}
                onChange={handleFormChange}
                className="form-input"
                required
                aria-label="Selecione a loja"
              >
                <option value="">Selecione a Loja</option>
                {/* CORREÇÃO: Usar funcionariosPorLoja */}
                {Object.keys(funcionariosPorLoja).sort().map(loja => (
                  <option key={loja} value={loja}>{loja}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="data" className="label">Data do atestado</label>
              <input
                id="data"
                type="date"
                name="data"
                value={formData.data}
                onChange={handleFormChange}
                className="form-input"
                required
                aria-label="Data do atestado"
              />
            </div>
            {/* Campo de Funcionário — combo COM BUSCA (2026-07-28). Era um
                <select> puro: com centenas de nomes só dava para rolar até
                achar. Agora dá para digitar um trecho do nome (sem acento). */}
            <div className="form-group">
              <label htmlFor="nomeFuncionario" className="label">Funcionário</label>
              <ComboBusca
                className="form-input"
                required
                valor={formData.nomeFuncionario}
                placeholder="Digite ou escolha o funcionário…"
                opcoes={funcionariosAtivos.map(f => ({ key: f.nome, label: f.nome, valor: f.nome }))}
                onMudar={(nome) => handleFormChange({ target: { name: 'nomeFuncionario', value: nome } })}
              />
            </div>

            <div className="form-group">
              <label htmlFor="nomeCargo" className="label">Cargo</label>
              <select 
                id="nomeCargo" 
                name="nomeCargo" 
                value={formData.nomeCargo} 
                onChange={handleFormChange} 
                className="form-input" 
                required
                disabled={formData.nomeFuncionario !== ''} // TRAVA: Bloqueia se o funcionário já foi escolhido
                style={{ backgroundColor: formData.nomeFuncionario !== '' ? '#eee' : '#fff' }}
              >
                <option value="">Selecione um Cargo</option>
                {cargos.map(nome => <option key={nome} value={nome}>{nome}</option>)}
              </select>
            </div>
            
            {renderPerguntas()}
                  
                  <button type="submit" style={{ width: '100%', padding: '16px', backgroundColor: '#4fed3a', color: '#fff', fontSize: '16px', fontWeight: 'bold', border: 'none', borderRadius: '8px', cursor: 'pointer', marginTop: '20px' }}>
                      Finalizar e Enviar
                  </button>
              </form>
          </div>

        </div>
      </div>
    </div>
  );
}

export default MedicinaAtestadosLancamento;